// Internal analytics — completely independent of Meta Pixel/CAPI.
// Fires events to POST /api/analytics-events/event with visitor_id + session_id
// + user_id + UTM + fbclid. On failure, buffers to localStorage and retries.

const VISITOR_KEY = "spp_visitor_id";
const SESSION_KEY = "spp_session_id";
const BUFFER_KEY = "spp_analytics_buffer";
const MAX_BUFFER = 50; // keep buffer bounded

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Stable identifiers ───────────────────────────────────────────────
function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function getVisitorId() {
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) {
      v = uuid();
      localStorage.setItem(VISITOR_KEY, v);
    }
    return v;
  } catch {
    return "";
  }
}

export function getSessionId() {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = uuid();
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return "";
  }
}

// ─── UTM + fbclid parsing (persisted once per session) ───────────────
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const UTM_SESSION_KEY = "spp_utm_context";

function parseUtmContext() {
  try {
    // If already captured this session, return it
    const cached = sessionStorage.getItem(UTM_SESSION_KEY);
    if (cached) return JSON.parse(cached);

    const params = new URLSearchParams(window.location.search);
    const ctx = {};
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) ctx[k] = v;
    }
    const fbclid = params.get("fbclid");
    if (fbclid) ctx.fbclid = fbclid;

    if (Object.keys(ctx).length > 0) {
      sessionStorage.setItem(UTM_SESSION_KEY, JSON.stringify(ctx));
    }
    return ctx;
  } catch {
    return {};
  }
}

// ─── Retry buffer (localStorage) ──────────────────────────────────────
function readBuffer() {
  try {
    const raw = localStorage.getItem(BUFFER_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeBuffer(events) {
  try {
    // Cap buffer size — keep the most recent
    const trimmed = events.slice(-MAX_BUFFER);
    localStorage.setItem(BUFFER_KEY, JSON.stringify(trimmed));
  } catch {
    // localStorage full or blocked — silently drop
  }
}

function pushToBuffer(evt) {
  const buf = readBuffer();
  buf.push(evt);
  writeBuffer(buf);
}

async function flushBuffer() {
  const buf = readBuffer();
  if (buf.length === 0) return;
  try {
    const res = await fetch(`${API_URL}/api/analytics-events/event/batch`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ events: buf }),
      credentials: "omit",
    });
    if (res.ok) {
      writeBuffer([]); // clear on success
    }
    // If fails, keep buffer intact — next call will retry
  } catch {
    // Network failure — keep buffer
  }
}

// ─── Public API ───────────────────────────────────────────────────────
/**
 * Fire an internal analytics event. Non-blocking — errors never propagate.
 * @param {string} eventName    e.g. "LandingPageView", "PurchaseCompleted"
 * @param {object} customData   Optional: { product_id, plan_name, order_value, metadata, meta_event_id, user_id }
 */
export function trackInternalEvent(eventName, customData = {}) {
  if (!API_URL) return;

  const utm = parseUtmContext();
  const payload = {
    event_name: eventName,
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    user_id: customData.user_id || null,
    page_url: typeof window !== "undefined" ? window.location.href : "",
    referrer: typeof document !== "undefined" ? document.referrer : "",
    ...utm,
    meta_event_id: customData.meta_event_id || "",
    product_id: customData.product_id || "",
    plan_name: customData.plan_name || "",
    order_value: customData.order_value ?? null,
    metadata: customData.metadata || null,
    client_timestamp: new Date().toISOString(),
  };

  // Fire-and-forget with retry buffer fallback
  (async () => {
    try {
      const res = await fetch(`${API_URL}/api/analytics-events/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "omit",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Opportunistic flush of any queued events after a success
      flushBuffer();
    } catch {
      pushToBuffer(payload);
    }
  })();
}

// Flush any queued events on page load
if (typeof window !== "undefined") {
  window.addEventListener("load", () => {
    setTimeout(flushBuffer, 1500);
  });
}
